import { Component, computed, effect, inject, input, OnInit, output, Signal, signal, WritableSignal } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ModalProductItem } from "@components/floorplan-item-attachments-modal/floorplan-item-details-modal.component";
import { LocalizeGasNamePipe } from "@domain/gas/gas";
import { GasService } from "@domain/gas/gas.service";
import { ProductService } from "@domain/product/product.service";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanTransmitterDetailFormComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-transmitter-detail-form/floorplan-transmitter-detail-form.component";
import { ReferenceMeasureService } from "@project/floorplanner/floorplan-additional-notes/reference-measure-notes/reference-measure.service";
import { FloorplanItemAttachmentsButtonComponent } from "@project/floorplanner/floorplan-item-attachments-button/floorplan-item-attachments-button.component";
import { FloorplanItemGroupComponent } from "@project/floorplanner/floorplan-item-group/floorplan-item-group.component";
import { ProductItem } from "@project/product-item";
import { ProjectStateService } from "@project/project-state.service";
import { EMPTY, map, Observable } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: " app-transmitter-notes",
  templateUrl: "./transmitter-notes.component.html",
  styleUrls: ["./transmitter-notes.component.scss"],
  imports: [
    UiKitModule,
    FloorplanItemDetailsComponent,
    FloorplanTransmitterDetailFormComponent,
    FloorplanItemGroupComponent,
    FloorplanItemAttachmentsButtonComponent,
    ReactiveFormsModule,
  ],
  providers: [LocalizeGasNamePipe],
  standalone: true,
})
export class TransmitterNotesComponent implements OnInit {
  private readonly gasService = inject(GasService);
  private readonly localizeGasName = inject(LocalizeGasNamePipe);
  private readonly productService = inject(ProductService);
  private readonly projectStateService = inject(ProjectStateService);
  private readonly referenceMeasureService: ReferenceMeasureService = inject(ReferenceMeasureService);

  readonly floorplanItem = input.required<FloorplanItem>();
  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly toggleExtendedButtonClicked = output<void>();

  protected readonly transmitter = computed(() => this.floorplanItem() as FloorplanTransmitter);
  protected readonly isDiscontinued: WritableSignal<boolean> = signal(false);
  protected readonly isIncompleteConfiguration: WritableSignal<boolean> = signal(false);
  protected readonly productItems: Signal<ModalProductItem[]> = computed(() => this.getProductItems(this.transmitter()));
  protected readonly attachments: Signal<ModalProductItem[]> = computed(() => this.transmitter().config.attachments);

  protected gasName$!: Observable<string>;
  protected sensorName$!: Observable<string>;

  protected readonly SidesheetExtent = FloorplanSidesheetExtent;

  constructor() {
    effect(() => {
      const transmitter = this.transmitter();
      this.projectStateService
        .isConfigDiscontinued(transmitter.config.id)
        .pipe(take(1))
        .subscribe((isDiscontinued) => {
          this.isDiscontinued.set(isDiscontinued);
        });
      this.gasName$ = this.resolveGasName();
      this.sensorName$ = this.resolveSensorName();
      this.projectStateService
        .isConfigIncomplete(transmitter.config.id)
        .pipe(take(1))
        .subscribe((isIncompleteConfiguration) => {
          this.isIncompleteConfiguration.set(isIncompleteConfiguration);
        });
    });
  }

  ngOnInit() {
    this.referenceMeasureService.referenceMeasureUpdate$.subscribe(() => {
      this.transmitter().updateMonitoringAreaRadiusByReferenceMeasure();
    });
  }

  protected getProductItems(transmitter: FloorplanTransmitter): ModalProductItem[] {
    const items: ModalProductItem[] = [
      {
        ...ProductItem.create(transmitter.config.productId),
        detectionRange: transmitter.config.defaultDetectionRange,
      },
    ];
    if (transmitter.config.sensorId) {
      items.push(ProductItem.create(transmitter.config.sensorId));
    }
    return items;
  }

  private resolveGasName(): Observable<string> {
    if (!this.transmitter().config.gasId) {
      return EMPTY;
    }
    return this.gasService.getGasById(this.transmitter().config.gasId!).pipe(
      take(1),
      map((gas) => (gas ? this.localizeGasName.transform(gas) : this.transmitter().config.gasId!)),
    );
  }

  private resolveSensorName(): Observable<string> {
    if (!this.transmitter().config.sensorId) {
      return EMPTY;
    }
    return this.productService.getProductNameById(this.transmitter().config.sensorId!).pipe(
      take(1),
      map((name) => name || this.transmitter().config.sensorId!),
    );
  }
}
